/**
 * What to check before a processed document is kept.
 *
 * None of this blocks a save. The model read the document; the operator is the
 * one who can see the page, and a document that genuinely has no order number
 * must still be storable. So a problem here is a warning with the save still
 * one click away — the point is that nobody stores a run without having been
 * told what looks wrong with it.
 *
 * The thresholds deliberately match what the result screen already shows: the
 * same 60% that colours a confidence tag red is the one that raises a warning,
 * so the dialog never contradicts the table behind it.
 */
import type { SavedField } from '../../types/document-intelligence.types';

/** Below this, the screen already paints the confidence tag red. */
const LOW_CONFIDENCE = 0.6;

export interface SaveWarning {
  /** Groups the list; also what decides the wording of the heading. */
  kind: 'category' | 'missing' | 'type' | 'confidence';
  message: string;
}

/** A date the way documents actually write them: 12/03/2024, 2024-03-12,
 *  12-03-2024. Month names are left to `Date.parse`. */
const NUMERIC_DATE = /^\d{1,4}[-/.\s]\d{1,2}[-/.\s]\d{1,4}$/;

/** Arabic-Indic (U+0660-0669) and Eastern Arabic-Indic (U+06F0-06F9) digits as 0-9. */
export const toWesternDigits = (text: string): string =>
  text.replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06F0));

/** Does the value look like the type the category says it is?
 *
 *  Generous on purpose: a false alarm costs a sentence in a dialog, while a
 *  missed one lets "N/A" into a field something downstream will parse.
 */
const fitsType = (raw: string, dataType: string): boolean => {
  // Arabic documents write numbers in Arabic-Indic digits (٠١٢ / ۰۱۲). They are
  // the same numbers, so they are checked as 0-9 rather than flagged as wrong.
  const value = toWesternDigits(raw.trim());
  if (!value) return true;                       // emptiness is the missing check's job
  switch (dataType) {
    case 'Integer':
      return /^[+-]?\d+$/.test(value.replace(/[,\s]/g, ''));
    case 'Decimal':
      return /^[+-]?\d*\.?\d+$/.test(value.replace(/[,\s]/g, ''));
    case 'Date':
      return NUMERIC_DATE.test(value) || !Number.isNaN(Date.parse(value));
    case 'Boolean':
      return /^(true|false|yes|no|y|n|1|0)$/i.test(value);
    default:
      return true;                               // String takes anything
  }
};

/** Everything worth telling the operator before this run is stored. */
export const reviewBeforeSave = (fields: SavedField[], category: string): SaveWarning[] => {
  const warnings: SaveWarning[] = [];

  if (!category || category === 'Unknown') {
    warnings.push({
      kind: 'category',
      message: 'This document could not be matched to a category, so no fields were extracted. '
        + 'It will be saved as unidentified.',
    });
  }

  fields.forEach((field) => {
    const value = field.value === null || field.value === undefined ? '' : String(field.value);

    if (field.required && (field.missing || !value.trim())) {
      warnings.push({ kind: 'missing', message: `${field.fieldName} is required but has no value.` });
      return;                                    // no point type-checking nothing
    }
    if (value && !fitsType(value, field.dataType)) {
      warnings.push({
        kind: 'type',
        message: `${field.fieldName} should be ${field.dataType.toLowerCase()}, but reads "${value}".`,
      });
    }
    // Only worth raising for a value that is actually there: a missing field is
    // already its own warning, and a confident "not found" is still not found.
    if (value && !field.missing && field.confidence > 0 && field.confidence < LOW_CONFIDENCE) {
      warnings.push({
        kind: 'confidence',
        message: `${field.fieldName} was read with low confidence `
          + `(${Math.round(field.confidence * 100)}%) — check it against the document.`,
      });
    }
  });

  return warnings;
};
